/**
 * PayChargeEmbed - Main embed library class
 *
 * Extends native EventTarget for standard event handling.
 *
 * Usage:
 *   const embed = PayChargeEmbed.configure({
 *     containerId: 'payment-form',
 *     sessionToken: 'eyJ...'
 *   });
 *
 *   embed.addEventListener('charge:success', (e) => {
 *     console.log(e.detail.chargeId);
 *   });
 *
 *   await embed.initialize({ amount: 10.00 });
 */

import { IframeManager } from './IframeManager';
import { MessageBridge } from './MessageBridge';
import {
  PayChargeEmbedConfig,
  PayChargeMode,
  InitializeOptions,
  MESSAGE_TYPES,
  ChargeSuccessDetail,
  ChargeFailedDetail,
  FieldChangeDetail,
  ErrorDetail,
} from './types';

// Default iframe URL (relative to where the embed is hosted)
const DEFAULT_IFRAME_URL = '../clients/heartland/index.html';
const DEFAULT_API_URL = '/api/charge/v1';

export class PayChargeEmbed extends EventTarget {
  private config: PayChargeEmbedConfig | null = null;
  private iframeManager: IframeManager;
  private messageBridge: MessageBridge | null = null;
  private initialized = false;
  private amount: number = 0;
  private currency: string = 'USD';
  private mode: PayChargeMode = 'iframe';

  private constructor() {
    super();
    this.iframeManager = new IframeManager();
  }

  /**
   * Configure the embed with required options.
   * Returns a new PayChargeEmbed instance.
   */
  static configure(options: PayChargeEmbedConfig): PayChargeEmbed {
    const instance = new PayChargeEmbed();
    instance.mode = options.mode || 'iframe';
    instance.config = {
      ...options,
      mode: instance.mode,
      iframeUrl: options.iframeUrl || DEFAULT_IFRAME_URL,
      apiUrl: options.apiUrl || DEFAULT_API_URL,
    };

    return instance;
  }

  /**
   * Initialize and render the payment form.
   * Mode can be provided here to support integrator-controlled selection at runtime.
   * For iframe mode: Returns a promise that resolves when the form is ready.
   * For redirect mode: Redirects to the hosted checkout page (never resolves).
   */
  async initialize(options?: InitializeOptions): Promise<void> {
    if (!this.config) {
      throw new Error('[PayChargeEmbed] Must call configure() before initialize()');
    }

    const mode = options?.mode || this.mode;
    this.mode = mode;

    if (this.initialized) {
      console.warn('[PayChargeEmbed] Already initialized');
      return;
    }

    // Store amount and currency
    this.amount = options?.amount || 0;
    this.currency = options?.currency || 'USD';

    // Handle redirect mode
    if (mode === 'redirect') {
      return this.initializeRedirect();
    }

    // Iframe mode
    return this.initializeIframe(options);
  }

  /**
   * Initialize redirect mode - redirect to hosted checkout page.
   */
  private initializeRedirect(): Promise<void> {
    const sessionId = this.decodeSessionId(this.config!.sessionToken);
    if (!sessionId) {
      throw new Error('[PayChargeEmbed] Failed to extract session ID from token');
    }

    const baseUrl = this.config!.apiUrl?.replace(/\/api\/v1$/, '') || window.location.origin;
    const checkoutUrl = `${baseUrl}/checkout/?session=${sessionId}`;

    // Not logged. The checkout URL carries the session id, which authorises
    // the unauthenticated charge and cancel endpoints, and this runs on the
    // merchant's own page where a console-capturing SDK would ship it off-site.
    // NIT review of #1101.
    window.location.href = checkoutUrl;

    // This promise never resolves since we're redirecting
    return new Promise(() => {});
  }

  /**
   * Initialize iframe mode - embed the payment form.
   */
  private initializeIframe(options?: InitializeOptions): Promise<void> {
    if (!this.config!.containerId) {
      throw new Error('[PayChargeEmbed] containerId is required for iframe mode');
    }

    // Get container element
    const container = document.getElementById(this.config!.containerId!);
    if (!container) {
      throw new Error(`[PayChargeEmbed] Container not found: #${this.config!.containerId}`);
    }

    // Build iframe URL with embed mode params
    const iframeUrl = this.buildIframeUrl();

    // Set up message bridge
    this.messageBridge = new MessageBridge(iframeUrl);
    this.messageBridge.listen(this.handleMessage.bind(this));

    // Create promise that resolves when iframe is ready
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('[PayChargeEmbed] Timeout waiting for iframe to be ready'));
      }, 30000);

      // Listen for ready event
      const onReady = () => {
        clearTimeout(timeout);
        this.initialized = true;
        resolve();
      };

      this.addEventListener('ready', onReady, { once: true });

      // Create and inject iframe
      this.iframeManager.create({
        src: iframeUrl,
        container,
        css: this.config!.iframeCss,
        onLoad: () => {
          // Set iframe window for message bridge
          const contentWindow = this.iframeManager.getContentWindow();
          if (contentWindow) {
            this.messageBridge!.setIframeWindow(contentWindow);

            // Send initial configuration
            this.messageBridge!.sendConfig({
              sessionToken: this.config!.sessionToken,
              apiUrl: this.config!.apiUrl!,
              amount: this.amount,
              currency: this.currency,
              labels: this.config!.labels,
              formLayout: this.config!.formLayout,
              parentOrigin: window.location.origin,
            });
          }
        },
      });
    });
  }

  /**
   * Update the payment amount
   */
  setAmount(amount: number): void {
    this.amount = amount;
    if (this.messageBridge && this.initialized) {
      this.messageBridge.sendAmountUpdate(amount);
    }
  }

  /**
   * Destroy the embed and clean up resources
   */
  destroy(): void {
    this.messageBridge?.destroy();
    this.iframeManager.destroy();
    this.initialized = false;
    this.config = null;
  }

  /**
   * Convenience method wrapping addEventListener.
   * Returns an unsubscribe function.
   */
  on<T = unknown>(event: string, callback: (detail: T) => void): () => void {
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent<T>;
      callback(customEvent.detail);
    };
    this.addEventListener(event, handler);
    return () => this.removeEventListener(event, handler);
  }

  /**
   * Get current configuration (read-only)
   */
  getConfig(): Readonly<PayChargeEmbedConfig> | null {
    return this.config ? { ...this.config } : null;
  }

  /**
   * Check if embed is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  // --- Private Methods ---

  /**
   * Build the iframe URL with embed mode parameters
   */
  private buildIframeUrl(): string {
    const baseUrl = this.config!.iframeUrl!;
    const url = new URL(baseUrl, window.location.origin);

    // Add embed mode flag
    url.searchParams.set('embed', 'true');

    // Add parent origin for postMessage validation
    url.searchParams.set('origin', window.location.origin);

    return url.toString();
  }

  /**
   * Handle incoming messages from the iframe
   */
  private handleMessage(type: string, payload: unknown): void {
    switch (type) {
      case MESSAGE_TYPES.READY:
        this.dispatch('ready', {});
        break;

      case MESSAGE_TYPES.FIELD_UPDATE:
        this.dispatch('field:change', payload as FieldChangeDetail);
        break;

      case MESSAGE_TYPES.RESIZE:
        const resizeData = payload as { height: number };
        this.iframeManager.setHeight(resizeData.height);
        this.dispatch('resize', resizeData);
        break;

      case MESSAGE_TYPES.CHARGE_SUCCESS:
        this.dispatch('charge:success', payload as ChargeSuccessDetail);
        break;

      case MESSAGE_TYPES.CHARGE_FAILED:
        this.dispatch('charge:failed', payload as ChargeFailedDetail);
        break;

      case MESSAGE_TYPES.ERROR:
        this.dispatch('error', payload as ErrorDetail);
        break;

      default:
        console.warn('[PayChargeEmbed] Unknown message type:', type);
    }
  }

  /**
   * Dispatch a custom event with detail
   */
  private dispatch<T>(eventName: string, detail: T): void {
    this.dispatchEvent(new CustomEvent(eventName, { detail }));
  }

  /**
   * Decode session ID from JWT token (without verification).
   * JWTs are base64url encoded, with payload in the second segment.
   */
  private decodeSessionId(token: string): string | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('[PayChargeEmbed] Invalid JWT format');
        return null;
      }

      // Decode base64url payload
      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      const parsed = JSON.parse(decoded);

      // Session ID is stored in 'sid' claim
      return parsed.sid || null;
    } catch (error) {
      console.error('[PayChargeEmbed] Failed to decode JWT:', error);
      return null;
    }
  }

  /**
   * Get the current mode (iframe or redirect)
   */
  getMode(): PayChargeMode {
    return this.mode;
  }
}
